import React, { PropsWithChildren, useRef } from 'react';
import classNames from 'classnames';
import { ClayUserBadge, InteractionTypeBadge } from '@repo/components/badges';
import Occasion from '@repo/utils/helpers/Occasion';
import { BadgeStarredIcon, NewContactIcon } from '@repo/icons';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import styles from './ContactCard.module.scss';
import { ID } from '@repo/utils/types';
import { useSelector } from 'react-redux';
import { isStarredSelector } from '@repo/redux/actions/starred';
import Contact from '@repo/utils/models/Contact';

const StarredStatus = ({ contactID }: { contactID: ID }) => {
  const starred = useRef(null);
  const notStarred = useRef(null);
  const contactIsStarred = useSelector(isStarredSelector({ id: contactID } as Contact));
  const nodeRef = contactIsStarred ? starred : notStarred;

  return (
    <SwitchTransition mode="in-out">
      <CSSTransition
        key={String(contactIsStarred)}
        nodeRef={nodeRef}
        addEndListener={(done) => {
          nodeRef.current.addEventListener('transitionend', done, false);
        }}
        classNames={{
          enter: styles.badgeEnter,
          enterActive: styles.badgeEnterActive,
          exit: styles.badgeExit,
          exitActive: styles.badgeExitActive,
        }}
      >
        <span ref={nodeRef} className={styles.starredBadgeWrapper}>
          <span
            className={classNames(
              styles.starredBadge,
              contactIsStarred ? styles.starred : styles.notStarred,
            )}
          >
            {contactIsStarred && <BadgeStarredIcon className={styles.starred} />}
          </span>
        </span>
      </CSSTransition>
    </SwitchTransition>
  );
};

function NewContactBadge({ contact }: { contact: Contact }) {
  let created = null;

  if (contact.objectID) {
    created = Occasion.fromTimestamp(contact.created);
  } else {
    created = new Occasion(contact.created);
  }

  if (created.isWithin(7)) {
    return <NewContactIcon />;
  }

  return null;
}

const IconsAccessoryWrapper = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <span className={classNames(styles.accessory, styles.icons, className)}>{children}</span>
);

export function IconsAccesory({
  contact,
  className = null,
  image = false,
  interaction = true,
  children = null,
  noWrapper = false,
}) {
  let Wrapper = IconsAccessoryWrapper;
  let props: { className?: string } = { className };

  if (noWrapper) {
    Wrapper = React.Fragment;
    props = {};
  }

  return (
    <Wrapper {...props}>
      <StarredStatus contactID={contact.id} />
      <NewContactBadge contact={contact} />
      {!interaction ? null : (
        <InteractionTypeBadge interaction={contact.interactionType || contact.interaction_type} />
      )}
      <ClayUserBadge image={image} contact={contact} icon />
      {children}
    </Wrapper>
  );
}
